// Copyright (c) StrangeBee 2023

import { ReactElement } from 'react';
import { useLocalMessage } from '@Helpers/';
import { useListAllUsers } from '@Queries/userQueries';
import listInputByType from '@Molecules/InputItem';
import { DescribeFieldAttribute, SearchFormInterface, ObservableTypeOutput } from 'thehive-sdk';
import useListQuery from '@Hooks/useListQuery';
import client from '@Client';

interface CustomValueInputProps {
    attribute?: DescribeFieldAttribute;
    className?: string;
    disabled?: boolean;
    value: any;
    filter?: Partial<SearchFormInterface>;
    onChange: (newValue: any, params?: SearchFormInterface['params']) => void;
}

const CustomValueInput = ({
    attribute,
    className,
    disabled = false,
    value,
    onChange,
    filter,
}: CustomValueInputProps): ReactElement | null => {
    const i = useLocalMessage();
    const { data: userList = [] } = useListAllUsers();

    const { data: observableTypeList = [] } = useListQuery<ObservableTypeOutput[]>({
        queryParams: {
            selector: {
                name: 'list-all-observable-types',
                head: [{ name: 'listObservableType' }],
            },
        },
        controller: client.ObservableTypeController,
        fetchOnStart: true,
        manual: true,
    });

    const { values = [], name = '', type = 'boolean', labels = [] } = attribute || {};

    if (!attribute) {
        return null;
    }

    let valueList = values;
    let labelList = labels;
    const nameList = name;
    let typeList = type;

    if (type === 'user') {
        const usersOptions =
            userList.map((user) => ({
                value: user.login,
                label: user.name,
            })) || [];

        valueList = usersOptions?.map(({ value }) => value);
        labelList = usersOptions?.map(({ label }) => label);
        typeList = 'user';
    }

    if (name === 'dataType') {
        valueList = observableTypeList?.map(({ name }) => name);
        labelList = observableTypeList?.map(({ name }) => name);
    }

    if (valueList?.length && !['user'].includes(type)) {
        typeList = 'enumeration';
    }

    if (['number', 'integer', 'float'].includes(typeList)) {
        typeList = 'number';
    }

    return (listInputByType[nameList] || listInputByType[typeList] || listInputByType.boolean)({
        value,
        onChange,
        className,
        disabled,
        labelList,
        valueList,
        i,
        attribute,
        filter,
        userList,
    });
};

export default CustomValueInput;
